import React, { useState, useEffect, useCallback } from 'react';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';
import { useKeycloak } from '@react-keycloak/web';
import {
  ShieldCheckIcon,
  PlusIcon,
  BuildingLibraryIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import type {
  TrustAccount,
  TrustAccountType,
  TrustReconciliationStatus,
} from '../types/billing.types';

const API_BASE = '';

const ACCOUNT_TYPE_LABELS: Record<TrustAccountType, string> = {
  iolta_pooled: 'IOLTA Pooled',
  dedicated_client: 'Dedicated Client',
  escrow: 'Escrow',
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const getReconStatusBadge = (status: TrustReconciliationStatus) => {
  switch (status) {
    case 'current':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">
          <CheckCircleIcon className="h-3 w-3" /> Current
        </span>
      );
    case 'overdue':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <ExclamationTriangleIcon className="h-3 w-3" /> Overdue
        </span>
      );
    case 'never_reconciled':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300">
          <ClockIcon className="h-3 w-3" /> Never Reconciled
        </span>
      );
  }
};

const TrustAccountsPage: React.FC = () => {
  const navigate = useAnimatedNavigate();
  const { keycloak } = useKeycloak();
  const [accounts, setAccounts] = useState<TrustAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState<TrustAccountType>('iolta_pooled');
  const [formBankName, setFormBankName] = useState('');
  const [formJurisdiction, setFormJurisdiction] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${keycloak?.token || ''}`,
  }), [keycloak?.token]);

  const fetchAccounts = useCallback(async () => {
    if (!keycloak?.token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/billing/trust-accounts`, { headers: getHeaders() });
      if (!res.ok) throw new Error(`Failed to fetch trust accounts: ${res.statusText}`);
      const json = await res.json();
      setAccounts(json.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch trust accounts');
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  }, [keycloak?.token, getHeaders]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const handleCreateAccount = async () => {
    if (!formName.trim()) { setFormError('Account name is required.'); return; }
    if (!formBankName.trim()) { setFormError('Bank name is required.'); return; }
    setFormSubmitting(true);
    setFormError(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/billing/trust-accounts`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          account_name: formName,
          account_type: formType,
          bank_name: formBankName,
          state_bar_jurisdiction: formJurisdiction || undefined,
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.description || json?.error || 'Failed to create trust account');
      setShowCreateForm(false);
      setFormName(''); setFormBankName(''); setFormJurisdiction(''); setFormType('iolta_pooled');
      await fetchAccounts();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create trust account');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Summary stats
  const totalBalance = accounts.reduce((s, a) => s + Number(a.current_book_balance), 0);
  const activeCount = accounts.filter(a => a.is_active).length;
  const overdueCount = accounts.filter(a => a.reconciliation_status === 'overdue').length;

  return (
    <div className="max-w-7xl mx-auto px-6 py-6 space-y-6 lex-tab-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheckIcon className="h-7 w-7 text-teal-600 dark:text-teal-400" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Trust Accounts</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">IOLTA & client trust account management</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/billing/trust-reports')}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-teal-600 dark:text-teal-400 border border-teal-300 dark:border-teal-600 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
          >
            Reports
          </button>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            New Trust Account
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg border border-teal-200 dark:border-teal-800">
          <div className="text-teal-600 dark:text-teal-400 text-sm font-medium">Total Trust Balance</div>
          <div className="text-2xl font-bold text-teal-900 dark:text-teal-100">{formatCurrency(totalBalance)}</div>
          <div className="text-xs text-teal-500 dark:text-teal-400 mt-1">{activeCount} active account{activeCount !== 1 ? 's' : ''}</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
          <div className="text-green-600 dark:text-green-400 text-sm font-medium">Active Accounts</div>
          <div className="text-2xl font-bold text-green-900 dark:text-green-100">{activeCount}</div>
        </div>
        <div className={`p-4 rounded-lg border ${overdueCount > 0 ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}>
          <div className={`text-sm font-medium ${overdueCount > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}`}>Reconciliation Alerts</div>
          <div className={`text-2xl font-bold ${overdueCount > 0 ? 'text-amber-900 dark:text-amber-100' : 'text-gray-900 dark:text-white'}`}>{overdueCount}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{overdueCount > 0 ? 'Overdue reconciliations' : 'All current'}</div>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-teal-50 dark:bg-teal-900/10 border border-teal-200 dark:border-teal-800 rounded-lg p-5 space-y-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Create Trust Account</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Name *</label>
              <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="e.g. Main IOLTA Account"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Type</label>
              <select value={formType} onChange={(e) => setFormType(e.target.value as TrustAccountType)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                <option value="iolta_pooled">IOLTA Pooled</option>
                <option value="dedicated_client">Dedicated Client</option>
                <option value="escrow">Escrow</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bank Name *</label>
              <input type="text" value={formBankName} onChange={(e) => setFormBankName(e.target.value)} placeholder="e.g. First National Bank"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Jurisdiction</label>
              <input type="text" value={formJurisdiction} onChange={(e) => setFormJurisdiction(e.target.value)} placeholder="e.g. CA, NY, TX"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
          </div>
          {formError && <p className="text-sm text-red-500">{formError}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowCreateForm(false); setFormError(null); }}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
              Cancel
            </button>
            <button onClick={handleCreateAccount} disabled={formSubmitting}
              className="px-4 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
              {formSubmitting ? 'Creating...' : 'Create Account'}
            </button>
          </div>
        </div>
      )}

      {/* Accounts List */}
      {!initialLoadDone ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-6 w-6 border-2 border-teal-500 border-t-transparent rounded-full" />
          <span className="ml-2 text-sm text-gray-500">Loading trust accounts...</span>
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-500">{error}</div>
      ) : accounts.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <BuildingLibraryIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-500 dark:text-gray-400 text-sm">No trust accounts yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {accounts.map((account) => (
            <div
              key={account.trust_account_id}
              onClick={() => navigate(`/billing/trust-accounts/${account.trust_account_id}`)}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <BuildingLibraryIcon className="h-6 w-6 text-teal-600 dark:text-teal-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900 dark:text-white">{account.account_name}</h3>
                      <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        {ACCOUNT_TYPE_LABELS[account.account_type]}
                      </span>
                      {!account.is_active && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300">Inactive</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {account.bank_name}
                      {account.account_number_masked && ` \u2022 ${account.account_number_masked}`}
                      {account.state_bar_jurisdiction && ` \u2022 ${account.state_bar_jurisdiction}`}
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      {getReconStatusBadge(account.reconciliation_status)}
                      {account.last_reconciled_date && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Last reconciled: {formatDate(account.last_reconciled_date)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(Number(account.current_book_balance))}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Book Balance</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrustAccountsPage;
